
import React, { useState, useEffect } from "react";
import { marked } from "marked";
import "./QualityCheckForm.css";
import Button from "../Button/Button";
import { Sparkles, ChevronDown, ChevronRight } from "lucide-react";
import { checkPermission } from "../../utilities/hasPermission";

const AgentTreaningForm = ({
  handleSubmit,
  setAlertValue,
  aiAnalizeData = { call_summary: "", qa_feedback: "", voc: "" },
  setAiAnalizeData,
}) => {
  const [summary, setSummary] = useState(aiAnalizeData?.call_summary || aiAnalizeData?.summary || "");
  const [qaFeedback, setQaFeedback] = useState(aiAnalizeData?.qa_feedback || "");
  const [voc, setVoc] = useState(aiAnalizeData?.voc || "");

  // Collapsible state — first section open by default
  const [collapsed, setCollapsed] = useState({
    summary: false,
    voc: true,
    qaFeedback: true,
  });

  const [editFields, setEditFields] = useState({
    summary: aiAnalizeData?.editSummary || false,
    qaFeedback: aiAnalizeData?.editQaFeedback || false,
    voc: aiAnalizeData?.editVoc || false,
  });

  useEffect(() => {
    setSummary(aiAnalizeData?.call_summary || aiAnalizeData?.summary || "");
    setQaFeedback(aiAnalizeData?.qa_feedback || "");
    setVoc(aiAnalizeData?.voc || "");
    setEditFields({
      summary: aiAnalizeData?.editSummary || false,
      qaFeedback: aiAnalizeData?.editQaFeedback || false,
      voc: aiAnalizeData?.editVoc || false,
    });
  }, [aiAnalizeData]);

  const toggleCollapse = (fieldKey) => {
    setCollapsed((prev) => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
  };

  const toggleEdit = (field) => {
    setEditFields((prev) => {
      const updated = { ...prev, [field]: !prev[field] };
      setAiAnalizeData((prevData) => ({
        ...prevData,
        editSummary: updated.summary,
        editQaFeedback: updated.qaFeedback,
        editVoc: updated.voc,
        call_summary: summary,
        qa_feedback: qaFeedback,
        voc: voc,
      }));
      return updated;
    });
  };

  const handleDraftSave = () => {
    setAiAnalizeData({
      ...aiAnalizeData,
      call_summary: summary,
      qa_feedback: qaFeedback,
      voc: voc,
      editSummary: editFields.summary,
      editQaFeedback: editFields.qaFeedback,
      editVoc: editFields.voc,
    });
    setAlertValue({
      show: true,
      message: "Data has been saved as a draft.",
      variant: "success",
      position: "middle",
    });
  };

  const handleFinalSubmit = () => {
    const updatedData = {
      ...aiAnalizeData,
      call_summary: summary,
      qa_feedback: qaFeedback,
      voc: voc,
      editSummary: editFields.summary,
      editQaFeedback: editFields.qaFeedback,
      editVoc: editFields.voc,
    };
    setAiAnalizeData(updatedData);
    handleSubmit({ aiAnalizeData: [updatedData] });
  };

  const handleFieldChange = (fieldKey, value) => {
    const dataKey = {
      summary: "call_summary",
      qaFeedback: "qa_feedback",
      voc: "voc",
    }[fieldKey];

    if (fieldKey === "summary") setSummary(value);
    else if (fieldKey === "qaFeedback") setQaFeedback(value);
    else if (fieldKey === "voc") setVoc(value);

    setAiAnalizeData((previous) => ({
      ...previous,
      [dataKey]: value,
    }));
  };

  const renderMarkdown = (text) => ({ __html: marked.parse(text || "") });

  const renderField = (label, value, fieldKey) => (
    <div className={`ai-collapsible-section ${collapsed[fieldKey] ? "collapsed" : ""}`}>
      <div className="ai-collapsible-header" onClick={() => toggleCollapse(fieldKey)}>
        <div className="ai-collapsible-title">
          {collapsed[fieldKey] ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          <Sparkles size={14} />
          <span>{label}</span>
          <span className="powered-badge">Powered by AI</span>
        </div>
        <button
          className="edit-btn"
          onClick={(e) => {
            e.stopPropagation();
            if (collapsed[fieldKey]) setCollapsed((prev) => ({ ...prev, [fieldKey]: false }));
            toggleEdit(fieldKey);
          }}
        >
          {editFields[fieldKey] ? "Save" : "Edit"}
        </button>
      </div>

      {!collapsed[fieldKey] && (
        <div className="ai-collapsible-body">
          {editFields[fieldKey] ? (
            <textarea
              placeholder={`Add ${label}`}
              className="comment-box"
              value={value}
              onChange={(event) => handleFieldChange(fieldKey, event.target.value)}
            />
          ) : (
            <div className="formatted-text" dangerouslySetInnerHTML={renderMarkdown(value)} />
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="ai-summary-wrapper">
      <div className="ai-summary-scroll-area">
        <div className="quality-check-form">
          {renderField("Call Summary", summary, "summary")}
          {checkPermission.hasPermission(["voa:sidebar_business_insights"]) && renderField("Voice of Customer (VOC)", voc, "voc")}
          {renderField("QA Feedback", qaFeedback, "qaFeedback")}
        </div>
      </div>

      <div className="ai-summary-footer">
        <Button variant="outline" size="md" onClick={handleDraftSave}>
          Save As Draft
        </Button>
        <Button variant="primary" size="md" onClick={handleFinalSubmit} style={{ marginLeft: "auto" }}>
          Submit
        </Button>
      </div>
    </div>
  );
};

export default AgentTreaningForm;
